import { Injectable, OnDestroy } from '@angular/core';

import { Observable, Subject, Subscription } from 'rxjs';
import { io, Socket } from 'socket.io-client';

import { environment } from '@environment/environment';

@Injectable({ providedIn: 'root' })
export class SocketService implements OnDestroy {
    private socket: Socket;
    private connectionStatus = new Subject<boolean>();
    private connectionStatusSubscription: Subscription;

    constructor() {
        const currentHost = window.location.host;
        let wsUrl;
        if (currentHost.includes('localhost')) {
            wsUrl = environment.websocketUrl;
        } else {
            wsUrl = `${window.location.protocol}//${window.location.host}`;
        }
        this.socket = io(wsUrl, {
            reconnection: true,
            reconnectionAttempts: Infinity,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            timeout: 20000,
            transports: ['websocket', 'polling'],
        });

        this.socket.on('connect', () => {
            this.connectionStatus.next(true);
        });

        this.socket.on('disconnect', () => {
            this.connectionStatus.next(false);
            this.reconnect();
        });

        this.socket.on('connect_error', (error) => {
            this.connectionStatus.next(false);
        });

        this.socket.on('heartbeat', () => {});

        this.connectionStatusSubscription = this.connectionStatus
            .asObservable()
            .subscribe();
    }

    sendMessage(message: string): void {
        if (this.socket.connected) {
            this.socket.emit('message', message);
        }
    }

    onMessage(callback: (message: string) => void): void {
        this.socket.on('message', callback);
    }

    private lastJoinData: {
        classId: string;
        startDateTime: string;
        duration: number;
    };

    joinClass(data: {
        classId: string;
        startDateTime: string;
        duration: number;
    }): void {
        this.lastJoinData = data;

        if (!this.socket.connected) {
            this.socket.connect();
            this.socket.once('connect', () => {
                this.socket.emit('join-class', data);
            });
        } else {
            this.socket.emit('join-class', data);
        }
    }

    logToServer(
        level: 'info' | 'warn' | 'error',
        message: string,
        data?: any,
    ): void {
        if (this.socket.connected) {
            this.socket.emit('log-message', {
                level,
                message,
                data,
                timestamp: new Date().toISOString(),
            });
        }
    }

    onClassTerminated(callback: () => void): void {
        this.socket.off('class-terminated');
        this.socket.on('class-terminated', callback);
    }

    onTerminationWarning(callback: (minutesLeft: number) => void): void {
        this.socket.off('termination-warning');
        this.socket.on('termination-warning', callback);
    }

    terminateClass(classId: string): void {
        if (this.socket.connected) {
            this.socket.emit('terminate-class', classId);
        }
    }

    storeMeetingTimer(classId: string, startTime: Date): void {
        if (this.socket.connected) {
            this.socket.emit('store-timer', {
                classId,
                startTime: startTime.toISOString(),
            });
        }
    }

    getMeetingTimer(
        classId: string,
        callback: (startTime: Date | null) => void,
    ): void {
        if (this.socket.connected) {
            this.socket.emit('get-timer', classId);
            this.socket.once(
                'timer-retrieved',
                (data: { startTime: string | null }) => {
                    const startTime = data.startTime
                        ? new Date(data.startTime)
                        : null;
                    callback(startTime);
                },
            );
        } else {
            callback(null);
        }
    }

    deleteMeetingTimer(classId: string): void {
        if (this.socket.connected) {
            this.socket.emit('delete-timer', classId);
        }
    }

    lowerHand(participantId: string): void {
        if (this.socket.connected) {
            this.socket.emit('lower-hand', { participantId });
        }
    }

    onLowerHand(callback: (data: { participantId: string }) => void): void {
        this.socket.off('lower-hand');
        this.socket.on('lower-hand', callback);
    }

    getConnectionStatus(): Observable<boolean> {
        return this.connectionStatus.asObservable();
    }

    reconnect(): void {
        if (!this.socket.connected) {
            this.socket.connect();
        }
    }

    ngOnDestroy(): void {
        if (this.socket) {
            this.socket.removeAllListeners();
            this.socket.disconnect();
        }
        if (this.connectionStatusSubscription) {
            this.connectionStatusSubscription.unsubscribe();
        }
    }
}
